import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../store/hooks';
import { selectCurrentApplication } from '../../store/slices/application.slice';
import ReactFlow, {
  Node,
  Edge,
  Controls,
  MiniMap,
  Background,
  useNodesState,
  useEdgesState,
  MarkerType,
  Panel,
  useReactFlow,
  ReactFlowProvider,
  BackgroundVariant,
} from 'reactflow';
import 'reactflow/dist/style.css';
import dagre from 'dagre';
import MainLayout from '../../components/layout/MainLayout';
import ServiceNode from '../../components/service-map/ServiceNode';
import AnimatedEdge from '../../components/service-map/AnimatedEdge';
import ServiceDetailPanel from '../../components/service-map/ServiceDetailPanel';
import { serviceMapApi, Service, ServiceConnection } from '../../services/api/service-map.api';

const nodeTypes = {
  serviceNode: ServiceNode,
};

const edgeTypes = {
  animated: AnimatedEdge,
};

// Dagre layout configuration
const getLayoutedElements = (
  nodes: Node[],
  edges: Edge[],
  direction: 'TB' | 'LR' = 'TB'
) => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));

  const nodeWidth = 240;
  const nodeHeight = 180;
  const isHorizontal = direction === 'LR';

  dagreGraph.setGraph({
    rankdir: direction,
    ranksep: isHorizontal ? 150 : 120,
    nodesep: isHorizontal ? 80 : 100,
    edgesep: 50,
  });

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  const layoutedNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    return {
      ...node,
      position: {
        x: nodeWithPosition.x - nodeWidth / 2,
        y: nodeWithPosition.y - nodeHeight / 2,
      },
    };
  });

  return { nodes: layoutedNodes, edges };
};

function ServiceMapContent() {
  const { t } = useTranslation('apm');
  const { fitView } = useReactFlow();
  const selectedAppId = useAppSelector(selectCurrentApplication);

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [services, setServices] = useState<Service[]>([]);
  const [connections, setConnections] = useState<ServiceConnection[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'map' | 'list'>('map');
  const [layoutDirection, setLayoutDirection] = useState<'TB' | 'LR'>('TB');
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [highlightedPath, setHighlightedPath] = useState<Set<string>>(new Set());
  const [showLabels, setShowLabels] = useState(true);

  // Fetch data
  useEffect(() => {
    fetchData();
  }, [selectedAppId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const appId = selectedAppId || 'all';
      const mapData = await serviceMapApi.getServiceMap(appId);
      setServices(mapData.services);
      setConnections(mapData.connections);
    } catch (err: any) {
      setError(err.message || 'Failed to load service map');
      console.error('Service map fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Filter services
  const filteredServices = useMemo(() => {
    return services.filter((service) => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch =
          service.name.toLowerCase().includes(query) ||
          service.type.toLowerCase().includes(query) ||
          (service.environment && service.environment.toLowerCase().includes(query)) ||
          (service.language && service.language.toLowerCase().includes(query));
        if (!matchesSearch) return false;
      }
      if (statusFilter !== 'all' && service.status !== statusFilter) return false;
      if (typeFilter !== 'all' && service.type !== typeFilter) return false;
      return true;
    });
  }, [services, searchQuery, statusFilter, typeFilter]);

  // Get unique types
  const availableTypes = useMemo(() => {
    const types = new Set(services.map((s) => s.type));
    return Array.from(types).sort();
  }, [services]);

  // Calculate stats
  const stats = useMemo(() => {
    const healthy = services.filter(s => s.status === 'healthy').length;
    const warning = services.filter(s => s.status === 'warning').length;
    const critical = services.filter(s => s.status === 'critical').length;
    const totalCalls = connections.reduce((sum, c) => sum + (c.calls_per_minute || 0), 0);
    const avgLatency = connections.length > 0
      ? connections.reduce((sum, c) => sum + (c.avg_latency_ms || 0), 0) / connections.length
      : 0;
    const avgErrorRate = connections.length > 0
      ? connections.reduce((sum, c) => sum + (c.error_rate || 0), 0) / connections.length
      : 0;

    return { healthy, warning, critical, totalCalls, avgLatency, avgErrorRate };
  }, [services, connections]);

  // Handle service click
  const handleServiceClick = useCallback((service: Service) => {
    setSelectedService(service);

    // Highlight connected paths
    const connectedIds = new Set<string>();
    connectedIds.add(service.id);

    connections.forEach((conn) => {
      if (conn.source_service_id === service.id || conn.target_service_id === service.id) {
        connectedIds.add(conn.source_service_id);
        connectedIds.add(conn.target_service_id);
      }
    });

    setHighlightedPath(connectedIds);
  }, [connections]);

  // Handle panel close
  const handlePanelClose = useCallback(() => {
    setSelectedService(null);
    setHighlightedPath(new Set());
  }, []);

  // Navigate to service from panel
  const handleNavigateToService = useCallback((serviceId: string) => {
    const service = services.find(s => s.id === serviceId);
    if (service) {
      handleServiceClick(service);
    }
  }, [services, handleServiceClick]);

  // Get connection counts for a service
  const getConnectionCounts = useCallback((serviceId: string) => {
    let incoming = 0;
    let outgoing = 0;
    connections.forEach((conn) => {
      if (conn.target_service_id === serviceId) incoming++;
      if (conn.source_service_id === serviceId) outgoing++;
    });
    return { incoming, outgoing, total: incoming + outgoing };
  }, [connections]);

  // Build React Flow elements
  useEffect(() => {
    if (filteredServices.length === 0) {
      setNodes([]);
      setEdges([]);
      return;
    }

    const visibleServiceIds = new Set(filteredServices.map((s) => s.id));

    // Create nodes
    const flowNodes: Node[] = filteredServices.map((service) => {
      const counts = getConnectionCounts(service.id);
      const isSelected = selectedService?.id === service.id;
      const isHighlighted = highlightedPath.has(service.id);
      const isDimmed = highlightedPath.size > 0 && !isHighlighted;

      return {
        id: service.id,
        type: 'serviceNode',
        data: {
          ...service,
          connectionsCount: counts.total,
          incomingCount: counts.incoming,
          outgoingCount: counts.outgoing,
          isSelected,
          isHighlighted,
          isDimmed,
          onClick: handleServiceClick,
          errorRate: service.metadata?.error_rate || 0,
          avgLatency: service.metadata?.avg_latency_ms || 0,
          requestsPerSec: service.metadata?.calls_per_minute ? service.metadata.calls_per_minute / 60 : 0,
        },
        position: { x: 0, y: 0 },
      };
    });

    // Create edges
    const flowEdges: Edge[] = connections
      .filter(
        (conn) =>
          visibleServiceIds.has(conn.source_service_id) &&
          visibleServiceIds.has(conn.target_service_id)
      )
      .map((conn) => {
        const isHighlighted =
          highlightedPath.has(conn.source_service_id) &&
          highlightedPath.has(conn.target_service_id);
        const isDimmed = highlightedPath.size > 0 && !isHighlighted;

        return {
          id: `${conn.source_service_id}-${conn.target_service_id}`,
          source: conn.source_service_id,
          target: conn.target_service_id,
          type: 'animated',
          data: {
            callsPerMinute: conn.calls_per_minute || 0,
            avgLatency: conn.avg_latency_ms || 0,
            errorRate: conn.error_rate || 0,
            protocol: conn.protocol,
            isHighlighted,
            isDimmed,
          },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: conn.error_rate > 5 ? '#ef4444' : '#6366f1',
          },
        };
      });

    // Apply layout
    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
      flowNodes,
      flowEdges,
      layoutDirection
    );

    setNodes(layoutedNodes);
    setEdges(layoutedEdges);

    setTimeout(() => {
      fitView({ padding: 0.15, duration: 400 });
    }, 50);
  }, [
    filteredServices,
    connections,
    layoutDirection,
    selectedService,
    highlightedPath,
    getConnectionCounts,
    handleServiceClick,
    fitView,
    setNodes,
    setEdges,
  ]);

  const onLayout = useCallback((direction: 'TB' | 'LR') => {
    setLayoutDirection(direction);
  }, []);

  const onFitView = useCallback(() => {
    fitView({ padding: 0.15, duration: 400 });
  }, [fitView]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
        return 'bg-emerald-500';
      case 'warning':
        return 'bg-amber-500';
      case 'critical':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-[80vh]">
          <div className="flex flex-col items-center gap-4">
            <div className="relative w-16 h-16">
              <div className="absolute inset-0 rounded-full border-4 border-indigo-200 dark:border-indigo-900"></div>
              <div className="absolute inset-0 rounded-full border-4 border-indigo-500 border-t-transparent animate-spin"></div>
            </div>
            <p className="text-gray-600 dark:text-gray-400 font-medium">{t('serviceMap.loading')}</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <div className="rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-6">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/50 flex items-center justify-center">
              <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-red-800 dark:text-red-200">{t('serviceMap.error')}</h3>
              <p className="text-red-700 dark:text-red-300">{error}</p>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
              <div className="p-2 rounded-xl bg-indigo-500/10">
                <svg className="w-8 h-8 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
              </div>
              {t('serviceMap.title')}
            </h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400">{t('serviceMap.subtitle')}</p>
          </div>

          {/* View Mode Toggle */}
          <div className="flex items-center gap-3">
            <button
              onClick={fetchData}
              className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              title={t('serviceMap.refresh')}
            >
              <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
            <div className="flex rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              <button
                onClick={() => setViewMode('map')}
                className={`px-4 py-2 text-sm font-medium flex items-center gap-2 transition-colors ${
                  viewMode === 'map'
                    ? 'bg-indigo-500 text-white'
                    : 'bg-white text-gray-700 dark:bg-gray-800 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                </svg>
                {t('serviceMap.mapView')}
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-4 py-2 text-sm font-medium flex items-center gap-2 border-l border-gray-200 dark:border-gray-700 transition-colors ${
                  viewMode === 'list'
                    ? 'bg-indigo-500 text-white'
                    : 'bg-white text-gray-700 dark:bg-gray-800 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
                {t('serviceMap.listView')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-6">
        <div className="rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4 relative overflow-hidden group hover:shadow-lg transition-shadow">
          <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-indigo-500/10 to-transparent rounded-bl-full" />
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('serviceMap.stats.total')}</p>
          <p className="mt-1 text-3xl font-bold text-gray-900 dark:text-white">{services.length}</p>
          <p className="text-xs text-gray-400 mt-1">{t('serviceMap.services')}</p>
        </div>

        <div className="rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-emerald-500/10 to-transparent rounded-bl-full" />
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('serviceMap.stats.healthy')}</p>
          <p className="mt-1 text-3xl font-bold text-emerald-600 dark:text-emerald-400">{stats.healthy}</p>
          <div className="flex items-center gap-1 mt-1">
            <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-xs text-emerald-600 dark:text-emerald-400">{t('serviceMap.status.operational')}</span>
          </div>
        </div>

        <div className="rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-amber-500/10 to-transparent rounded-bl-full" />
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('serviceMap.stats.warning')}</p>
          <p className="mt-1 text-3xl font-bold text-amber-600 dark:text-amber-400">{stats.warning}</p>
          <div className="flex items-center gap-1 mt-1">
            <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
            <span className="text-xs text-amber-600 dark:text-amber-400">{t('serviceMap.status.degraded')}</span>
          </div>
        </div>

        <div className="rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-red-500/10 to-transparent rounded-bl-full" />
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('serviceMap.stats.critical')}</p>
          <p className="mt-1 text-3xl font-bold text-red-600 dark:text-red-400">{stats.critical}</p>
          <div className="flex items-center gap-1 mt-1">
            <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
            <span className="text-xs text-red-600 dark:text-red-400">{t('serviceMap.status.outage')}</span>
          </div>
        </div>

        <div className="rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-blue-500/10 to-transparent rounded-bl-full" />
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('serviceMap.stats.throughput')}</p>
          <p className="mt-1 text-3xl font-bold text-gray-900 dark:text-white">
            {stats.totalCalls > 1000 ? `${(stats.totalCalls / 1000).toFixed(1)}k` : stats.totalCalls}
          </p>
          <p className="text-xs text-gray-400 mt-1">{t('serviceMap.stats.callsPerMin')}</p>
        </div>

        <div className="rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-purple-500/10 to-transparent rounded-bl-full" />
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('serviceMap.stats.avgLatency')}</p>
          <p className={`mt-1 text-3xl font-bold ${
            stats.avgLatency > 500 ? 'text-red-600 dark:text-red-400' :
            stats.avgLatency > 200 ? 'text-amber-600 dark:text-amber-400' :
            'text-gray-900 dark:text-white'
          }`}>
            {stats.avgLatency.toFixed(0)}
          </p>
          <p className="text-xs text-gray-400 mt-1">ms</p>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-wrap items-center gap-4">
        {/* Search */}
        <div className="flex-1 min-w-[200px] max-w-md relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder={t('serviceMap.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
          />
        </div>

        {/* Status Filter */}
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
        >
          <option value="all">{t('serviceMap.filters.allStatus')}</option>
          <option value="healthy">{t('serviceMap.status.healthy')}</option>
          <option value="warning">{t('serviceMap.status.warning')}</option>
          <option value="critical">{t('serviceMap.status.critical')}</option>
        </select>

        {/* Type Filter */}
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
        >
          <option value="all">{t('serviceMap.filters.allTypes')}</option>
          {availableTypes.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>

        {/* Label Toggle */}
        <button
          onClick={() => setShowLabels(!showLabels)}
          className={`px-4 py-2.5 rounded-xl border transition-colors flex items-center gap-2 ${
            showLabels
              ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400'
              : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400'
          }`}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
          </svg>
          {t('serviceMap.labels')}
        </button>
      </div>

      {/* Main Content */}
      {services.length === 0 ? (
        <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-16 text-center">
          <div className="max-w-md mx-auto">
            <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">{t('serviceMap.noServices')}</h3>
            <p className="text-gray-500 dark:text-gray-400 mb-6">{t('serviceMap.noServicesDescription')}</p>
            <button
              onClick={fetchData}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-indigo-500 text-white font-medium hover:bg-indigo-600 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {t('serviceMap.refresh')}
            </button>
          </div>
        </div>
      ) : viewMode === 'map' ? (
        <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-xl overflow-hidden">
          <div style={{ height: '75vh' }}>
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              nodeTypes={nodeTypes}
              edgeTypes={edgeTypes}
              fitView
              attributionPosition="bottom-left"
              minZoom={0.1}
              maxZoom={2}
              defaultEdgeOptions={{
                type: 'animated',
              }}
            >
              <Background
                variant={BackgroundVariant.Dots}
                gap={20}
                size={1}
                color="#94a3b8"
                className="opacity-30"
              />
              <Controls
                className="!bg-white dark:!bg-gray-800 !border-gray-200 dark:!border-gray-700 !rounded-xl !shadow-lg"
              />
              <MiniMap
                nodeColor={(node) => {
                  const service = node.data as Service;
                  switch (service.status) {
                    case 'healthy':
                      return '#10b981';
                    case 'warning':
                      return '#f59e0b';
                    case 'critical':
                      return '#ef4444';
                    default:
                      return '#6b7280';
                  }
                }}
                maskColor="rgba(0, 0, 0, 0.1)"
                className="!bg-white/80 dark:!bg-gray-800/80 !rounded-xl !border !border-gray-200 dark:!border-gray-700"
              />

              {/* Layout Controls Panel */}
              <Panel position="top-right" className="space-y-3">
                <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm p-4 shadow-xl">
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                    {t('serviceMap.layout.title')}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => onLayout('TB')}
                      className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                        layoutDirection === 'TB'
                          ? 'bg-indigo-500 text-white'
                          : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                      </svg>
                      {t('serviceMap.layout.vertical')}
                    </button>
                    <button
                      onClick={() => onLayout('LR')}
                      className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                        layoutDirection === 'LR'
                          ? 'bg-indigo-500 text-white'
                          : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                      </svg>
                      {t('serviceMap.layout.horizontal')}
                    </button>
                  </div>
                  <button
                    onClick={onFitView}
                    className="mt-3 w-full flex items-center justify-center gap-2 rounded-lg bg-gray-100 dark:bg-gray-700 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                    </svg>
                    {t('serviceMap.layout.fitView')}
                  </button>
                </div>
              </Panel>

              {/* Legend Panel */}
              <Panel position="bottom-right">
                <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm p-4 shadow-xl">
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                    {t('serviceMap.legend.title')}
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">{t('serviceMap.status.healthy')}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">{t('serviceMap.status.warning')}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-red-500"></div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">{t('serviceMap.status.critical')}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-gray-500"></div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">{t('serviceMap.status.unknown')}</span>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {t('serviceMap.legend.clickHint')}
                    </p>
                  </div>
                </div>
              </Panel>

              {/* Info Panel */}
              {filteredServices.length !== services.length && (
                <Panel position="top-left">
                  <div className="rounded-xl border border-indigo-200 dark:border-indigo-800 bg-indigo-50/95 dark:bg-indigo-900/30 backdrop-blur-sm p-3 shadow-lg">
                    <p className="text-sm text-indigo-700 dark:text-indigo-300">
                      {t('serviceMap.showingFiltered', { count: filteredServices.length, total: services.length })}
                    </p>
                  </div>
                </Panel>
              )}
            </ReactFlow>
          </div>
        </div>
      ) : (
        /* List View */
        <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800">
              <thead className="bg-gray-50 dark:bg-gray-800/50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    {t('serviceMap.table.serviceName')}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    {t('serviceMap.table.type')}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    {t('serviceMap.table.status')}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    {t('serviceMap.table.environment')}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    {t('serviceMap.table.language')}
                  </th>
                  <th className="px-6 py-4 text-center text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    {t('serviceMap.table.connections')}
                  </th>
                  <th className="px-6 py-4 text-center text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    {t('serviceMap.table.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-800 dark:bg-gray-900">
                {filteredServices.map((service) => {
                  const counts = getConnectionCounts(service.id);
                  return (
                    <tr
                      key={service.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors"
                      onClick={() => handleServiceClick(service)}
                    >
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                            service.status === 'healthy' ? 'bg-emerald-100 dark:bg-emerald-900/30' :
                            service.status === 'warning' ? 'bg-amber-100 dark:bg-amber-900/30' :
                            service.status === 'critical' ? 'bg-red-100 dark:bg-red-900/30' :
                            'bg-gray-100 dark:bg-gray-800'
                          }`}>
                            <div className={`w-3 h-3 rounded-full ${getStatusColor(service.status)}`} />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-gray-900 dark:text-white">{service.name}</p>
                            {service.version && (
                              <p className="text-xs text-gray-500 dark:text-gray-400">v{service.version}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300 capitalize">
                          {service.type}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold ${
                          service.status === 'healthy'
                            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                            : service.status === 'warning'
                            ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                            : service.status === 'critical'
                            ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                            : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
                        }`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${getStatusColor(service.status)}`} />
                          {t(`serviceMap.status.${service.status}`)}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                        {service.environment || '-'}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                        {service.language || '-'}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <span className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400">
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                            </svg>
                            {counts.incoming}
                          </span>
                          <span className="inline-flex items-center gap-1 text-xs text-purple-600 dark:text-purple-400">
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                            </svg>
                            {counts.outgoing}
                          </span>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-center">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleServiceClick(service);
                          }}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 text-sm font-medium hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          {t('serviceMap.table.viewDetails')}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Service Detail Panel */}
      <ServiceDetailPanel
        service={selectedService}
        connections={connections}
        allServices={services}
        isOpen={!!selectedService}
        onClose={handlePanelClose}
        onServiceClick={handleNavigateToService}
      />
    </MainLayout>
  );
}

export default function ServiceMapPage() {
  return (
    <ReactFlowProvider>
      <ServiceMapContent />
    </ReactFlowProvider>
  );
}
